import { useCallback, useEffect, useState } from 'react';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);


export const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false
    },
  },
  elements: { point: { radius: 0 } },
  scales: {
    // to remove the labels
    x: {
      ticks: {
        display: false,
      },
      border: {
        display: false
      },
      // to remove the x-axis grid
      grid: {
        drawBorder: false,
        display: false,
      },
    },
    // to remove the y-axis labels
    y: {
      ticks: {
        display: false,
        beginAtZero: true,
      },
      border: {
        display: false
      },
      // to remove the y-axis grid
      grid: {
        drawBorder: false,
        display: false,
      },
    },
  },
};


interface CurrencyData {
  TYPE: string;
  IMAGEURL: string;
  FROMSYMBOL: string;
  PRICE: number;
  TOSYMBOL: string;
  FLAGS: string;
  CHANGEPCT24HOUR: number;
}

interface CryptocurrencyData {
  USD: CurrencyData;
  // Add other currency codes if needed
}

interface Cryptocurrency {
  BTC: CryptocurrencyData;
  ETH: CryptocurrencyData;
  USDT: CryptocurrencyData;
}

const initialData: Cryptocurrency = {
  "BTC": {
    "USD": {
      // Initial values for BTC USD data
      TYPE: "",
      IMAGEURL: "",
      FROMSYMBOL: "",
      PRICE: 60000,
      TOSYMBOL: "",
      FLAGS: "",
      CHANGEPCT24HOUR: 0.00,
      // Initialize other properties with appropriate types
    }
  },
  "ETH": {
    "USD": {
      // Initial values for ETH USD data
      TYPE: "",
      IMAGEURL: "",
      FROMSYMBOL: "",
      PRICE: 3000,
      TOSYMBOL: "",
      FLAGS: "",
      CHANGEPCT24HOUR: 0.00,
      // Initialize other properties with appropriate types
    }
  },
  "USDT": {
    "USD": {
      // Initial values for USDT USD data
      TYPE: "",
      IMAGEURL: "",
      FROMSYMBOL: "",
      PRICE: 1,
      TOSYMBOL: "",
      FLAGS: "",
      CHANGEPCT24HOUR: 0.00,
      // Initialize other properties with appropriate types
    }
  }
};

const FirstSection = () => {
  const [prices, setPrices] = useState<Cryptocurrency>(initialData);
  const [btcChart, setbtcChart] = useState<any>([]);
  const [ethchart, setethChart] = useState<any>([]);
  const [usdtChart, setusdtChart] = useState<any>([]);

  const [loading, setLoading] = useState<boolean>(true);

  const tokenPriceScan = async () => {
    try {
      // const response = await fetch('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=1&sparkline=true&price_change_percentage=1h%2C7d%2C14d%2C30d%2C200d%2C1y');
      const response = await fetch('https://min-api.cryptocompare.com/data/pricemultifull?fsyms=BTC,ETH,USDT&tsyms=USD');
      // const response = await fetch('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=10&page=1&sparkline=false');

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      let data = await response.json();
      setPrices(data.RAW);

      const btcData = await fetch('https://min-api.cryptocompare.com/data/v2/histohour?fsym=BTC&tsym=USD&limit=50');
      if (!btcData.ok) {
        throw new Error('Network response BTC chart was not ok');
      }

      data = await btcData.json()
      let realData = data.Data.Data
      setbtcChart({
        labels: realData.map((point: any) => new Date(point.time * 1000).toLocaleTimeString()),
        datasets: [
          {
            fill: true,
            data: realData.map((point: any) => point.close),
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
          },
        ],
      });

      const ethData = await fetch('https://min-api.cryptocompare.com/data/v2/histohour?fsym=ETH&tsym=USD&limit=50');
      if (!ethData.ok) {
        throw new Error('Network response ETH chart was not ok');
      }
      data = await ethData.json();
      realData = data.Data.Data

      setethChart({
        labels: realData.map((point: any) => new Date(point.time * 1000).toLocaleTimeString()),
        datasets: [
          {
            fill: true,
            data: realData.map((point: any) => point.close),
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
          },
        ],
      });

      const usdtData = await fetch('https://min-api.cryptocompare.com/data/v2/histohour?fsym=USDT&tsym=USD&limit=50');
      if (!usdtData.ok) {
        throw new Error('Network response USDT chart was not ok');
      }
      data = await usdtData.json();
      realData = data.Data.Data
      setusdtChart({
        labels: realData.map((point: any) => new Date(point.time * 1000).toLocaleTimeString()),
        datasets: [
          {
            fill: true,
            data: realData.map((point: any) => point.close),
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
          },
        ],
      });

      setLoading(false)

    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false)
    }
  }

  const init = useCallback(async () => {
    await tokenPriceScan()
  }, [])

  useEffect(() => {
    init()
  }, [init]);

  return (
    <>
      <div className="relative h-auto lg:h-[100vh] z-10" id="first-section">
        {/* TOP PART */}
        <div className='relative overflow-hidden pt-[150px] md:pt-[250px] pb-[50px] md:pb-[165px] px-[15px]'>
          <img className="first-bg-shape1 hidden lg:block" src="data:image/png;base64,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" alt="" />
          <img className="first-bg-shape2 hidden lg:block" src="data:image/png;base64,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" alt="" />
          <img className="first-bg-shape3 hidden lg:block" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAC4AAAAlCAYAAAA9ftv0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAUGSURBVHgB1ZhPchtVEMa/dlSVrW4g3wCdgOgGzpZVFHZhZRepokhVypMCyoTCsbFJhaIgioDAggXyCWJOEJ8AxRfAYhcSz+v0+98zGklJ7NjOq4xn5r03M7/u93X3U4D3tNGiCX/+wE+ZcVACdz66Qc9wQdpc8L+2uP3yMv611wJPDDziF2LA2vkbsDRv8HkLXWNA9mAj8AZ9voSnf+zy+uPvuYNzbEsLxnv2j3jbH8beoC2GrOMY+4+3uI9zaovArwgwO29b9mCAvTGMZel++Os9/ue3e3wNZ9zmavz3HXawrrFjpnAvd/6a/R2Y8EyMKT6+SUOcQZsJLtDW209sUCLAR2jXF2/9KhCiDYy/UeL69c/fbQDPlIoAuMBM+uYkFXJyt0FbysF5Tph/xRDGP2/w4Kcv310AzwQXiB5CQLLXdISzfQ6W4PTPIQ5sH8cMJPP7Vj4/fsWDBwUv45TbTKn8sslHcmo7I3xA2r/Ju0Qe0o6rOMhjUT5ZSoWs0PBGcToSagQffMPdJcnXLv1FJp4+R0jn7dBHOQacpfEd7I04lPvvPrlD2zhhmyWVbvpglEc+c9Q7vFQorUiUSpCWeDjHhzeuI8fW/XUe3799shrQCC7uWlEBySpAEXSf9KyMopRtgiE6C7E3hEIh68hjg53bPN55SwMapfLwLo9FAp2ahrMEgiSsjkmBxbG6rGo1YCpe5N+BGLO29jXt423BBxu8bNNZ+rAqMuHEMbdXAIPW01Q1rt7FalWIlVHkz8PyJYqbm3SIBW1KKqXkb8Qyz8GBUbM57dW9ylYG/kIdnFOp8SnSwYbcn+pC2ELYWOnTJYw3P+PBxur8FDoFLg/30tJnPVe0XS9KWlLKi3FjxjB5Bdy9Ag5O0u8kLnGt1cL42095IEfntcCXGB+amrei95FlwiYGpJ4bPF3rJ6MDOzuF4yrE6guTjbDXsvr90mD/7iqv1zkrGh8U8sOhhSOowDPTe5WsV1QLUMrtrPYxSFquzoOfp+4rAU7BOcjvOZT+4taO38RVwB98wT154ImuikBKa9DZowKKVF2Rhnk6SOtZJmUbEwyBkpoqYm4lsgPsLnStpcHJ4CooFxEFSI0Zwn7LOHUkr6ZvexjS88MgAUka2b5qta1mJ//MRC5GAj0sWziogEv7oL7/iC9MXzDKcfGlaunrS67AkxycDJAkSH53nyu0ahNWsMU2TeJABVyCoacLSpABK/j0QdbGIAdiU7Gp5XuYPE7Jo6WDd54Vw0bHDbC6JfDdgnsp6sMH7YtVcFYMQPZ2TH2xiob0o1ZMG2hCUJLXdrDfe9bMh20Ep1J+OKgB79pKkFCIdAqeyV5GXgGjjItjJq5cDkTr8Yn0jWRg+EJKfrG7GLYRXF60ogBiSeaka/KbqFBEvLHaML0CepVCgNrYkQubavcS7PabwTaCC0uXMrD7GNVSWtQ8kH4oR1AtG1LOsH1HHGCfnxB2CnzrFtv9SduEiFeFIwVRSAzQHtaZAmpMJk+kayTH8H86PdgpcHF31+3uuJZ7Q2NT86TJAWhXIGjKwu1Z2NP07HxwworhSsBlaK5kjJxx/Jz/5Dnv2TOAnQb3/xWRIj8CQ6U2pWErgz25OHPYCrjd9wr0sr3RXlepz1JbuJHs1M4VVrfW5ZaU+SZ5wGu2LC8OrG4tyc9XVXl2FaxcEtjjiwerW8vKRGAfOdjyYsNWWrHKbbyH7RUC0vLbckXj2gAAAABJRU5ErkJggg==" alt="" />
          <img className="first-bg-shape4 hidden lg:block" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAC4AAAAlCAYAAAA9ftv0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAUGSURBVHgB1ZhPchtVEMa/dlSVrW4g3wCdgOgGzpZVFHZhZRepokhVypMCyoTCsbFJhaIgioDAggXyCWJOEJ8AxRfAYhcSz+v0+98zGklJ7NjOq4xn5r03M7/u93X3U4D3tNGiCX/+wE+ZcVACdz66Qc9wQdpc8L+2uP3yMv611wJPDDziF2LA2vkbsDRv8HkLXWNA9mAj8AZ9voSnf+zy+uPvuYNzbEsLxnv2j3jbH8beoC2GrOMY+4+3uI9zaovArwgwO29b9mCAvTGMZel++Os9/ue3e3wNZ9zmavz3HXawrrFjpnAvd/6a/R2Y8EyMKT6+SUOcQZsJLtDW209sUCLAR2jXF2/9KhCiDYy/UeL69c/fbQDPlIoAuMBM+uYkFXJyt0FbysF5Tph/xRDGP2/w4Kcv310AzwQXiB5CQLLXdISzfQ6W4PTPIQ5sH8cMJPP7Vj4/fsWDBwUv45TbTKn8sslHcmo7I3xA2r/Ju0Qe0o6rOMhjUT5ZSoWs0PBGcToSagQffMPdJcnXLv1FJp4+R0jn7dBHOQacpfEd7I04lPvvPrlD2zhhmyWVbvpglEc+c9Q7vFQorUiUSpCWeDjHhzeuI8fW/XUe3799shrQCC7uWlEBySpAEXSf9KyMopRtgiE6C7E3hEIh68hjg53bPN55SwMapfLwLo9FAp2ahrMEgiSsjkmBxbG6rGo1YCpe5N+BGLO29jXt423BBxu8bNNZ+rAqMuHEMbdXAIPW01Q1rt7FalWIlVHkz8PyJYqbm3SIBW1KKqXkb8Qyz8GBUbM57dW9ylYG/kIdnFOp8SnSwYbcn+pC2ELYWOnTJYw3P+PBxur8FDoFLg/30tJnPVe0XS9KWlLKi3FjxjB5Bdy9Ag5O0u8kLnGt1cL42095IEfntcCXGB+amrei95FlwiYGpJ4bPF3rJ6MDOzuF4yrE6guTjbDXsvr90mD/7iqv1zkrGh8U8sOhhSOowDPTe5WsV1QLUMrtrPYxSFquzoOfp+4rAU7BOcjvOZT+4taO38RVwB98wT154ImuikBKa9DZowKKVF2Rhnk6SOtZJmUbEwyBkpoqYm4lsgPsLnStpcHJ4CooFxEFSI0Zwn7LOHUkr6ZvexjS88MgAUka2b5qta1mJ//MRC5GAj0sWziogEv7oL7/iC9MXzDKcfGlaunrS67AkxycDJAkSH53nyu0ahNWsMU2TeJABVyCoacLSpABK/j0QdbGIAdiU7Gp5XuYPE7Jo6WDd54Vw0bHDbC6JfDdgnsp6sMH7YtVcFYMQPZ2TH2xiob0o1ZMG2hCUJLXdrDfe9bMh20Ep1J+OKgB79pKkFCIdAqeyV5GXgGjjItjJq5cDkTr8Yn0jWRg+EJKfrG7GLYRXF60ogBiSeaka/KbqFBEvLHaML0CepVCgNrYkQubavcS7PabwTaCC0uXMrD7GNVSWtQ8kH4oR1AtG1LOsH1HHGCfnxB2CnzrFtv9SduEiFeFIwVRSAzQHtaZAmpMJk+kayTH8H86PdgpcHF31+3uuJZ7Q2NT86TJAWhXIGjKwu1Z2NP07HxwworhSsBlaK5kjJxx/Jz/5Dnv2TOAnQb3/xWRIj8CQ6U2pWErgz25OHPYCrjd9wr0sr3RXlepz1JbuJHs1M4VVrfW5ZaU+SZ5wGu2LC8OrG4tyc9XVXl2FaxcEtjjiwerW8vKRGAfOdjyYsNWWrHKbbyH7RUC0vLbckXj2gAAAABJRU5ErkJggg==" alt="" />
          <h1 className='text-white text-[34px] md:text-[70px] font-semibold leading-[1.2] text-center'>One-stop compliance solution<br /> for crypto business</h1>
          <p className='text-[18px] md:text-[24px] font-normal leading-[1.7] ibm-plex-sans text-center text-white mt-[41px] md:mt-[32px]'>We offer KYT/Wallet Screening, KYC, AML, and more for crypto businesses.</p>
          <div className='mt-[43px] md:mt-[88px] mb-[20px] text-center'><a className='bg-[#9568FF] text-white px-[48px] py-[18px] rounded-[8px] shadow-[0_4px_10px_rgba(0,191,255,0.2)]' href="#check">Get Started</a></div>
          <ul className='absolute hidden md:block max-w-[1200px] transform translate-x-[-50%] w-full h-full left-[50%] top-0 w-full z-[-1]'>
            <li className='absolute bottom-[-65px] left-[-180px] w-[550px] delay-1000 first-dolar'>
              <img src="/png/dolor-img01.png" alt="" />
            </li>
            <li className='absolute top-[80px] right-[-280px] w-[600px] delay-1000 first-dolar'>
              <img src="/png/dolor-img02.png" alt="" />
            </li>
          </ul>
        </div>

        {/* SOCIALS */}
        <div className="relative lg:absolute w-full bg-[#D9D6F1] lg:bg-[#0000] bottom-[-40px] left-0 py-[52px] lg:py-0">
          <div className='relative max-w-[1200px] m-auto social-blue-back px-[15px]'>
            <div className=' flex flex-wrap lg:flex-nowrap justify-center lg:justify-between px-0 md:px-[60px]'>
              <div className='w-full md:w-[50%] lg:w-[33.333%] px-0 sm:px-[15px]'>
                <div className='bg-white px-[25px] py-[20px] rounded-[10px] shadow-[0_15px_40px_rgba(1,18,111,0.15)]'>
                  <div className='flex gap-0 mb-[15px]'>
                    <div className='w-3/5 flex gap-0'>
                      <img className='w-1/3' src={"https://cryptocompare.com" + prices?.BTC.USD?.IMAGEURL} />
                      <div className='w-2/3 text-center'>
                        <div className='text-[#1c2e9e] text-[20px] font-semibold'>Bitcoin</div>
                        <div className='text-[14px] ibm-plex-sans'>{prices?.BTC.USD.FROMSYMBOL}</div>
                      </div>
                    </div>
                    <div className='w-2/5'>
                      { loading ? ('') : ( <Line data={btcChart} options={options} /> )}
                    </div>
                  </div>
                  <div className='flex justify-between items-center'>
                    <p className='text-[18px] text-[#1c2e9e] font-semibold'>${prices?.BTC.USD.PRICE?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className={`text-[15px] text-[#53C258] text-[${prices?.BTC.USD.CHANGEPCT24HOUR >= 0 ? "#53C258" : "#F22462"}] font-medium ibm-plex-sans`}>{prices?.BTC.USD.CHANGEPCT24HOUR?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</p>

                  </div>
                  <div className='flex justify-between text-[14px]'>
                    <p className='ibm-plex-sans'>Latest price</p>
                    <p className='ibm-plex-sans'>24h change</p>
                  </div>
                </div>
              </div>
              <div className='w-full md:w-[50%] lg:w-[33.333%] px-0 sm:px-[15px] mt-[30px] md:mt-0'>
                <div className='bg-white px-[25px] py-[20px] rounded-[10px] shadow-[0_15px_40px_rgba(1,18,111,0.15)]'>
                  <div className='flex gap-0 mb-[15px]'>
                    <div className='w-3/5 flex gap-0'>
                      <img className='w-1/3' src={"https://cryptocompare.com" + prices?.ETH.USD?.IMAGEURL} />
                      <div className='w-2/3 text-center'>
                        <div className='text-[#1c2e9e] text-[20px] font-semibold'>Ethereum</div>
                        <div className='text-[14px] ibm-plex-sans'>{prices?.ETH.USD.FROMSYMBOL}</div>
                      </div>
                    </div>
                    <div className='w-2/5'>
                      { loading ? ('') : ( <Line data={ethchart} options={options} /> ) }
                    </div>
                  </div>
                  <div className='flex justify-between'>
                    <p className='text-[18px] text-[#1c2e9e] font-semibold'>${prices?.ETH.USD.PRICE?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className={`text-[15px] text-[#53C258] text-[${prices?.ETH.USD.CHANGEPCT24HOUR >= 0 ? "#53C258" : "#F22462"}] font-medium ibm-plex-sans`}>{prices?.ETH.USD.CHANGEPCT24HOUR?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</p>
                  </div>
                  <div className='flex justify-between text-[14px]'>
                    <p className='ibm-plex-sans'>Latest price</p>
                    <p className='ibm-plex-sans'>24h change</p>
                  </div>
                </div>
              </div>
              <div className='w-full md:w-[50%] lg:w-[33.333%] px-0 sm:px-[15px] mt-[30px] lg:mt-0'>
                <div className='bg-white px-[25px] py-[20px] rounded-[10px] shadow-[0_15px_40px_rgba(1,18,111,0.15)]'>
                  <div className='flex gap-0 mb-[15px]'>
                    <div className='w-3/5 flex'>
                      <img className='w-1/3' src={"https://cryptocompare.com" + prices?.USDT.USD?.IMAGEURL} />
                      <div className='w-2/3 text-center'>
                        <div className='text-[#1c2e9e] text-[20px] font-semibold'>Tether</div>
                        <div className='text-[14px] ibm-plex-sans'>{prices?.USDT.USD.FROMSYMBOL}</div>
                      </div>
                    </div>
                    <div className='w-2/5'>
                      { loading ? ( '' ) : (<Line data={usdtChart} options={options} /> )}
                    </div>
                  </div>
                  <div className='flex justify-between'>
                    <p className='text-[18px] text-[#1c2e9e] font-semibold'>${prices?.USDT.USD.PRICE?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className={`text-[15px] text-[#53C258] text-[${prices?.USDT.USD.CHANGEPCT24HOUR >= 0 ? "#53C258" : "#F22462"}] font-medium ibm-plex-sans`}>{prices?.USDT.USD.CHANGEPCT24HOUR?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</p>
                  </div>
                  <div className='flex justify-between text-[14px]'>
                    <p className='ibm-plex-sans'>Latest price</p>
                    <p className='ibm-plex-sans'>24h change</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FirstSection;
